import moment from 'moment'
import React from 'react'
import PropTypes from 'prop-types'
import { Columns, Column, Button } from '@resurs/midas-react'
import { midas } from '@resurs/midas'
import { Conditional } from '@resurs/react-utils'
import { LocaleState } from '@merchant-portal/merchant-portal-base'
import { connect } from 'react-redux'
import { Countries } from '../../../../models/countries'
import { actOnData } from '../../../../util/helpers'
import HelpText from './HelpText'
import InfoESkatMandatory from './InfoESkatMandatory'
import PaymentMethodFormField from './PaymentMethodFormField'
import { getText } from '../../../../internal/TextLabels'
import Page from '../../../../internal/Page'
import {
  createDynamicSpecMap,
  FutureDateField,
  PastDateField,
  PredefinedFormFields,
} from './predefinedFormFields'
import styles from './payment-method-form.module.scss'

import {
  validateAge,
  validateNaturalGovernmentId,
} from '../../../../util/governmentIdUtils'
import { govIdComparison } from '../../../../util/govIdComparison'
import PushOutSwitch from '../../../../components/common/PushOutSwitch'
import PushFormToCustomer from './PushFormToCustomer'
import { Features, isEnabledForValue } from '../../../../models/features'
import QRModal from '../../../../components/common/QRModal'
import {
  clearCurrentRedirection,
  createApplicationOrPayment,
  getAutomaticDebitForPayment,
  prepareForRedirection,
  pushedOutToCustomer,
  pushOutCompleted,
} from '../../../../store/actions/pay-apply-actions'
import { PaymentMethods } from '../../../../models/payment-methods'

const deferredInput = delay => {
  let prevReject
  let timeout

  return val => {
    if (prevReject) {
      clearTimeout(timeout)
      prevReject()
      prevReject = null
    }
    return new Promise((resolve, reject) => {
      prevReject = () => {
        reject(val)
      }
      timeout = setTimeout(() => {
        prevReject = null
        resolve(val)
      }, delay)
    })
  }
}

const buildPushOutApplicationData = appData =>
  [
    PredefinedFormFields.APPLICANT_GOVERNMENT_ID,
    PredefinedFormFields.APPLICANT_MOBILE_NUMBER,
    PredefinedFormFields.CO_APPLICANT_CHECK,
    PredefinedFormFields.CO_APPLICANT_PHONE,
    PredefinedFormFields.CO_APPLICANT_ID,
  ].reduce(
    (formData, field) => ({
      ...formData,
      ...(appData[field] && { [field]: appData[field] }),
    }),
    {},
  )

class PaymentMethodForm extends React.PureComponent {
  constructor(props, context) {
    super(props, context)
    this.formRef = React.createRef()
    const appData = {}
    const valid = {}
    this.dynamicSpecMap = createDynamicSpecMap(
      this.context.country,
      this.props.formData,
    )
    this.deferredAddressInput = deferredInput(1200)
    this.props.formData
      .map(
        entry =>
          actOnData(this.dynamicSpecMap[entry.name], func => func({})) || entry,
      )
      .forEach(entry => {
        if (entry.name && entry.value) {
          appData[entry.name] = entry.value
        } else if (entry.name === 'approve-conditions' && !entry.value) {
          appData[entry.name] = true
        }
        if (
          entry.mandatory &&
          entry.format &&
          entry.name &&
          !entry.name.includes('coapplicant')
        ) {
          if (entry.value) {
            valid[entry.name] = /entry.format/.test(entry.value)
          } else {
            valid[entry.name] = false
          }
        }
      })
    this.state = {
      appData,
      valid,
      customHint: {},
      pushToCustomerForm:
        isEnabledForValue(
          Features.ACTIVATED_COUNTRIES_FOR_PUSH_OUT,
          this.props.countryCode,
        ) &&
        (this.props.hasPushOut ||
          this.props.selectedPaymentMethod === PaymentMethods.RESURS_INVOICE) &&
        localStorage.getItem('push-out-checked') === 'true',
      sendByTextMessage: false,
      coApplicantTextMessage: false,
      applicantQR: false,
      coApplicantQR: false,
      debounceNextClick: false,
    }
  }

  handleChange(field, value) {
    this.focusOnError = false
    this.setState(
      state => ({
        appData: {
          ...state.appData,
          [field]: value,
        },
      }),
      () => this.checkFaroeIslandsAddress(field, value),
    )
  }

  handleCoApplicantToggle() {
    const { CO_APPLICANT_CHECK, CO_APPLICANT_ID, CO_APPLICANT_PHONE } =
      PredefinedFormFields
    this.handleChange(
      CO_APPLICANT_CHECK,
      !this.state.appData[CO_APPLICANT_CHECK],
    )
    this.handleChange(CO_APPLICANT_ID, undefined)
    this.handleChange(CO_APPLICANT_PHONE, undefined)
    this.setState(state => ({
      valid: { ...state.valid, [CO_APPLICANT_ID]: false },
      coApplicantQR: false,
    }))
    this.props.clearRedirection()
  }

  handleModalTitle() {
    if (
      this.state.applicantQR &&
      (this.state.coApplicantQR ||
        !!this.props.redirectionResult?.coApplicant?.url)
    ) {
      return getText('payApply.pushToCustomer.linkApplicant')
    }
    if (this.state.coApplicantQR) {
      return getText('payApply.pushToCustomer.linkCoApplicant')
    }
    return getText('payApply.pushToCustomer.link')
  }

  handlePushOut(pushOutForm) {
    const { appData, sendToApplicant, sendToCoApplicant } = pushOutForm
    const preferredCommunication = {}

    if (sendToApplicant && !this.props.isPushedOut) {
      preferredCommunication.customer = {
        mobile: appData[PredefinedFormFields.APPLICANT_MOBILE_NUMBER],
        smsRequested: true,
      }
    }
    if (sendToCoApplicant && !this.props.isPushedOut) {
      preferredCommunication.coCustomer = {
        mobile: appData[PredefinedFormFields.CO_APPLICANT_PHONE],
        smsRequested: true,
      }
    }
    if (!this.props.isPushedOut) {
      this.props.setPushedOut()
    }
    if (this.props.redirectionResult) {
      return this.props
        .pushOutToCustomer(preferredCommunication)
        .then(() => this.props.pushOutComplete())
    }
    if (
      (this.state.applicantQR || this.state.coApplicantQR) &&
      !this.state.debounceNextClick
    ) {
      this.setState({ debounceNextClick: true })
      return this.props
        .createApplicationOrPayment(buildPushOutApplicationData(appData))
        .then(() => this.props.pushOutToCustomer(preferredCommunication))
    }
    if (!this.state.debounceNextClick) {
      this.setState({ debounceNextClick: true })
      return this.props
        .createApplicationOrPayment(buildPushOutApplicationData(appData))
        .then(() => this.props.pushOutToCustomer(preferredCommunication))
        .then(() => this.props.pushOutComplete())
    }
    return undefined
  }

  onApply(e) {
    e.preventDefault()
    if (!this.state.pushToCustomerForm && this.allFieldsValid()) {
      const applicationData = this.getActiveFields().reduce(
        (result, { name }) => ({
          ...result,
          [name]: this.state.appData[name],
        }),
        {},
      )
      this.props.onNext(applicationData)
    }
    if (
      this.state.valid[PredefinedFormFields.CO_APPLICANT_ID] &&
      !this.state.coApplicantQR &&
      !this.state.coApplicantTextMessage
    ) {
      return
    }
    if (
      this.state.pushToCustomerForm &&
      (this.state.sendByTextMessage || this.state.applicantQR)
    ) {
      const applicationData = this.getActiveFields().reduce(
        (result, { name }) => ({
          ...result,
          [name]: this.state.appData[name],
        }),
        {},
      )
      this.props.getAutomaticDebit().then(() =>
        this.handlePushOut({
          appData: applicationData,
          sendToApplicant: this.state.sendByTextMessage,
          sendToCoApplicant: this.state.coApplicantTextMessage,
        }),
      )
    }
  }

  async getCoappInfo() {
    const coApplicant = await this.props.getCoappInfo(
      this.state.appData[PredefinedFormFields.CO_APPLICANT_ID],
    )
    if (coApplicant) {
      const mobilePhoneNumberCoApp =
        coApplicant &&
        coApplicant.mobilePhoneNumber &&
        coApplicant.mobilePhoneNumber.value
          ? coApplicant.mobilePhoneNumber.value
          : null
      const emailCoApp =
        coApplicant && coApplicant.email ? coApplicant.email : null

      this.setState(state => ({
        appData: {
          ...state.appData,
          [PredefinedFormFields.CO_APPLICANT_PHONE]: mobilePhoneNumberCoApp,
          [PredefinedFormFields.CO_APPLICANT_EMAIL]: emailCoApp,
        },
        valid: {
          ...state.valid,
          [PredefinedFormFields.CO_APPLICANT_PHONE]: !!mobilePhoneNumberCoApp,
          [PredefinedFormFields.CO_APPLICANT_EMAIL]: !!emailCoApp,
        },
      }))
    }
  }

  /* eslint-disable class-methods-use-this */
  getHint(field, input) {
    if (field === PredefinedFormFields.CO_APPLICANT_ID) {
      if (!validateNaturalGovernmentId(this.context.country, input)) {
        return getText('payApply.applicant.governmentIdNotValidError')
      }

      if (!validateAge(this.context.country, input)) {
        return getText('payApply.applicant.underAgedCustomer')
      }

      if (
        !govIdComparison(
          this.state.appData[PredefinedFormFields.CO_APPLICANT_ID],
          this.state.appData[PredefinedFormFields.APPLICANT_GOVERNMENT_ID],
          this.context.country,
        )
      ) {
        return getText('payApply.coApplicant.uniqueGovId')
      }
    }
    if (this.context.country === Countries.DK) {
      if (FutureDateField[field]) {
        const dateFormat = `${input}`.length === 6 ? 'MMYYYY' : 'MMYY'
        if (moment(input, dateFormat).isBefore(moment())) {
          return getText('default.form.formatError.patternMismatch')
        }
      }
      if (PastDateField[field]) {
        const dateFormat = `${input}`.length === 6 ? 'MMYYYY' : 'MMYY'
        if (moment(input, dateFormat).isAfter(moment())) {
          return getText('default.form.formatError.patternMismatch')
        }
      }
    }

    return this.state.customHint[field] || null
  }
  /* eslint-enable  class-methods-use-this */

  getActiveFields() {
    return this.props.formData
      .filter(({ name, type }) => name && type !== 'heading')
      .map(({ name }) => this.dynamicSpecMap[name](this.state.appData))
      .filter(({ skipped }) => !skipped)
  }

  allFieldsValid() {
    return this.getActiveFields()
      .filter(({ hidden }) => !hidden)
      .reduce(
        (allValid, { name }) => allValid && !!this.state.valid[name],
        true,
      )
  }

  isValid(validation) {
    this.setState(state => ({
      valid: {
        ...state.valid,
        [validation.key]: validation.valid,
      },
    }))
  }

  checkFaroeIslandsAddress(field, value) {
    const addressFields = [
      PredefinedFormFields.APPLICANT_ADDRESS_STREET,
      PredefinedFormFields.APPLICANT_ADDRESS_POSTAL_CODE,
    ]
    if (
      !addressFields.includes(field) ||
      this.state.appData[PredefinedFormFields.APPLICANT_ADDRESS_COUNTRY] !==
        'FO'
    ) {
      return
    }
    this.setState(state => ({
      customHint: {
        ...state.customHint,
        [PredefinedFormFields.APPLICANT_ADDRESS_POSTAL_CODE]: '',
      },
    }))
    this.isValid({
      key: PredefinedFormFields.APPLICANT_ADDRESS_AREA,
      valid: false,
    })
    this.deferredAddressInput(value)
      .then(() => {
        const street =
          this.state.appData[PredefinedFormFields.APPLICANT_ADDRESS_STREET]
        const postalCode =
          this.state.appData[PredefinedFormFields.APPLICANT_ADDRESS_POSTAL_CODE]
        if (!street || !postalCode) {
          return
        }

        this.props
          .validateAddress(street, postalCode)
          .then(({ city }) => {
            this.handleChange(
              PredefinedFormFields.APPLICANT_ADDRESS_AREA,
              city.toLocaleUpperCase(),
            )
            this.isValid({
              key: PredefinedFormFields.APPLICANT_ADDRESS_AREA,
              valid: true,
            })
          })
          .catch(() => {
            this.handleChange(PredefinedFormFields.APPLICANT_ADDRESS_AREA, '')
            this.setState(state => ({
              customHint: {
                ...state.customHint,
                [PredefinedFormFields.APPLICANT_ADDRESS_POSTAL_CODE]: getText(
                  'payApply.paymentMethodForm.streetPostalCodeMismatch',
                ),
              },
            }))
          })
      })
      .catch(() => {})
  }

  autoFocusFirstField() {
    if (!this.autoFocused) {
      this.autoFocused = true
      return true
    }
    return false
  }

  render() {
    this.autoFocused = false
    const fields = this.props.formData
      .map(
        entry =>
          actOnData(this.dynamicSpecMap[entry.name], func =>
            func(this.state.appData),
          ) || entry,
      )
      .filter(entry => !entry.hidden)
    const helpTexts = fields.filter(entry => !!entry.helpText)
    const isESkatForm =
      this.props.formData.findIndex(({ name }) =>
        [
          PredefinedFormFields.ESKAT_CONSENT,
          PredefinedFormFields.APPLICANT_ESKAT_CONSENT,
        ].includes(name),
      ) >= 0

    return (
      <>
        <form
          ref={this.formRef}
          onSubmit={e => this.onApply(e)}
          onInvalid={e => {
            if (!this.focusOnError) {
              this.focusOnError = true
              e.target.focus()
              e.target.scrollIntoView(false)
            }
          }}
        >
          <Conditional when={isESkatForm}>
            <InfoESkatMandatory
              title={getText('payApply.paymentMethodForm.contact')}
            />
          </Conditional>
          <Page
            title={
              isESkatForm ? '' : getText('payApply.paymentMethodForm.contact')
            }
          >
            <div
              className={styles.container}
              style={{ padding: '0rem 2rem 2rem' }}
            >
              {isEnabledForValue(
                Features.ACTIVATED_COUNTRIES_FOR_PUSH_OUT,
                this.props.countryCode,
              ) &&
                (this.props.hasPushOut ||
                  this.props.selectedPaymentMethod ===
                    PaymentMethods.RESURS_INVOICE) && (
                  <div style={{ paddingTop: '2rem' }}>
                    <PushOutSwitch
                      checked={this.state.pushToCustomerForm}
                      onChange={() => {
                        localStorage.setItem(
                          'push-out-checked',
                          !this.state.pushToCustomerForm,
                        )
                        this.setState(state => ({
                          pushToCustomerForm: !state.pushToCustomerForm,
                        }))
                      }}
                    />
                  </div>
                )}
              {this.state.pushToCustomerForm ? (
                <Columns>
                  <Column col9>
                    <PushFormToCustomer
                      description={getText(
                        'payApply.pushToCustomer.description',
                      )}
                      mobileNumber={
                        this.state.appData[
                          PredefinedFormFields.APPLICANT_MOBILE_NUMBER
                        ]
                      }
                      onChangeMobileNumber={e =>
                        this.handleChange(
                          PredefinedFormFields.APPLICANT_MOBILE_NUMBER,
                          e.target.value,
                        )
                      }
                      sendByTextMessage={this.state.sendByTextMessage}
                      toggleTextMessage={() =>
                        this.setState(state => ({
                          sendByTextMessage: !state.sendByTextMessage,
                        }))
                      }
                      openWithQR={this.state.applicantQR}
                      toggleQR={() =>
                        this.setState(state => ({
                          applicantQR: !state.applicantQR,
                        }))
                      }
                      formHasCoApplicant={
                        !!fields.find(
                          field =>
                            field.name ===
                            PredefinedFormFields.CO_APPLICANT_CHECK,
                        )
                      }
                      toggleCoApplicant={() => this.handleCoApplicantToggle()}
                      coApplicantCheck={
                        this.state.appData[
                          PredefinedFormFields.CO_APPLICANT_CHECK
                        ]
                      }
                    />
                    {this.state.appData[
                      PredefinedFormFields.CO_APPLICANT_CHECK
                    ] && (
                      <PushFormToCustomer
                        coApplicant
                        header={getText('payApply.pushToCustomer.coApplicant')}
                        description={getText(
                          'payApply.pushToCustomer.description',
                        )}
                        mobileNumber={
                          this.state.appData[
                            PredefinedFormFields.CO_APPLICANT_PHONE
                          ]
                        }
                        onChangeMobileNumber={e =>
                          this.handleChange(
                            PredefinedFormFields.CO_APPLICANT_PHONE,
                            e.target.value,
                          )
                        }
                        governmentId={
                          this.state.appData[
                            PredefinedFormFields.CO_APPLICANT_ID
                          ]
                        }
                        onChangeGovernmentId={e =>
                          this.handleChange(
                            PredefinedFormFields.CO_APPLICANT_ID,
                            e.target.value,
                          )
                        }
                        sendByTextMessage={this.state.coApplicantTextMessage}
                        toggleTextMessage={() =>
                          this.setState(state => ({
                            coApplicantTextMessage:
                              !state.coApplicantTextMessage,
                          }))
                        }
                        openWithQR={this.state.coApplicantQR}
                        toggleQR={() =>
                          this.setState(state => ({
                            coApplicantQR: !state.coApplicantQR,
                          }))
                        }
                        getCoApplicantInfo={() => this.getCoappInfo()}
                        isValid={validation =>
                          this.isValid({
                            key: PredefinedFormFields.CO_APPLICANT_ID,
                            valid: validation.valid,
                          })
                        }
                        additionalValidation={input =>
                          this.getHint(
                            PredefinedFormFields.CO_APPLICANT_ID,
                            input,
                          )
                        }
                        disabled={
                          !this.state.valid[
                            PredefinedFormFields.CO_APPLICANT_ID
                          ]
                        }
                      />
                    )}
                  </Column>
                  <Column col3 />
                </Columns>
              ) : (
                <Columns>
                  <Column col6>
                    {fields.map(entry =>
                      entry.showHeading ? (
                        <h4 key={entry.name || entry.label}>{entry.label}</h4>
                      ) : (
                        <PaymentMethodFormField
                          key={entry.name || entry.label}
                          type={entry.type}
                          name={entry.name}
                          label={entry.label || ''}
                          description={entry.unit || ''}
                          options={entry.options}
                          focus={() => this.autoFocusFirstField()}
                          mandatory={entry.mandatory}
                          min={entry.min}
                          max={entry.max}
                          helpText={entry.inputHelpText}
                          format={{
                            regex: entry.format,
                            error:
                              entry.formatMessage ||
                              (!this.state.appData[entry.name] &&
                                getText('default.form.required')) ||
                              getText(
                                `payApply.paymentMethodForm.${entry.name}`,
                              ) ||
                              getText('payApply.paymentMethodForm.wrongFormat'),
                          }}
                          readonly={entry.readOnly ? entry.readOnly : false}
                          defaultValue={
                            this.state.appData[entry.name]
                              ? this.state.appData[entry.name]
                              : entry.defaultValue || null
                          }
                          isValid={validation => this.isValid(validation)}
                          onChange={value =>
                            this.handleChange(entry.name, value)
                          }
                          additionalValidation={input =>
                            this.getHint(entry.name, input)
                          }
                          disabled={entry.disabled}
                          showOnlyCurrencyNoRange={entry.usePlaceholderRange}
                        >
                          {entry.completeCoApplicantButton && (
                            <Button
                              size="small"
                              loading={this.props.onPageLoading}
                              disabled={!this.state.valid[entry.name]}
                              onClick={() => this.getCoappInfo()}
                            >
                              {getText(
                                'payApply.paymentMethodForm.getCoappInfoBtn',
                              )}
                            </Button>
                          )}
                        </PaymentMethodFormField>
                      ),
                    )}
                  </Column>
                  <Column col2 />
                  <Column col4>
                    {helpTexts.map(({ name, helpText }) => (
                      <HelpText key={`help-${name}`}>{helpText}</HelpText>
                    ))}
                  </Column>
                </Columns>
              )}
            </div>
          </Page>
          <Page>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '2rem',
              }}
              className={midas.pt10}
            >
              <Button
                id="payment-method-form-previous"
                onClick={() => this.props.onPrevious()}
              >
                {getText('payApply.wizard.previous')}
              </Button>
              <Button
                id="payment-method-form-btn"
                type="primary"
                form
                loading={this.props.newPageLoading}
                onClick={() => {
                  this.formRef.current.checkValidity()
                }}
              >
                {getText('payApply.paymentMethod.apply')}
              </Button>
            </div>
          </Page>
        </form>
        <QRModal
          show={
            (this.state.applicantQR &&
              this.props.redirectionResult?.customer.url) ||
            (this.state.coApplicantQR &&
              this.props.redirectionResult?.coApplicant?.url)
          }
          value={
            this.state.applicantQR
              ? this.props.redirectionResult?.customer.url
              : this.props.redirectionResult?.coApplicant?.url
          }
          title={this.handleModalTitle()}
          description={getText('payApply.pushToCustomer.scanQR')}
          onClose={() =>
            this.setState({ applicantQR: false, coApplicantQR: false })
          }
          onNext={() =>
            this.state.applicantQR && this.state.coApplicantQR
              ? this.setState({ applicantQR: false })
              : this.props.pushOutComplete()
          }
        />
      </>
    )
  }
}

PaymentMethodForm.propTypes = {
  hasPushOut: PropTypes.bool,
  formData: PropTypes.array.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  newPageLoading: PropTypes.bool,
  onPageLoading: PropTypes.bool.isRequired,
  getCoappInfo: PropTypes.func.isRequired,
  validateAddress: PropTypes.func.isRequired,
  createApplicationOrPayment: PropTypes.func.isRequired,
  pushOutComplete: PropTypes.func.isRequired,
  pushOutToCustomer: PropTypes.func.isRequired,
  clearRedirection: PropTypes.func.isRequired,
  setPushedOut: PropTypes.func.isRequired,
  selectedPaymentMethod: PropTypes.string,
  getAutomaticDebit: PropTypes.func.isRequired,
}

PaymentMethodForm.defaultProps = {
  hasPushOut: true,
  newPageLoading: false,
  selectedPaymentMethod: null,
}

const mapDispatchToProps = dispatch => ({
  createApplicationOrPayment: data =>
    dispatch(createApplicationOrPayment(data)),
  pushOutToCustomer: data =>
    dispatch(prepareForRedirection(false, false, false, data)),
  pushOutComplete: () => dispatch(pushOutCompleted()),
  clearRedirection: () => dispatch(clearCurrentRedirection()),
  setPushedOut: () => dispatch(pushedOutToCustomer()),
  getAutomaticDebit: () => dispatch(getAutomaticDebitForPayment()),
})

PaymentMethodForm.contextType = LocaleState

export default connect(null, mapDispatchToProps)(PaymentMethodForm)
